import React from 'react';
import { Tooltip } from '../../ui/Tooltip';
import type { FieldConfidence } from '../../../types/extraction';

interface ProvenanceTooltipProps {
  /** Snippet de provenance (absent si le LLM n'a pas fourni de source). */
  snippet?: string;
  /** Page du snippet (si snippet présent). */
  page?: number;
  /** Confiance du champ (couleur de la pastille + message si pas de snippet). */
  confidence?: FieldConfidence | null;
  /** Nom du champ (affiché dans l'en-tête). */
  fieldLabel?: string;
  /** Action de navigation/surbrillance dans le PDF (si snippet présent). */
  onClick?: () => void;
  /** @deprecated conservé pour compat — non utilisé (la pastille remplace p.N). */
  showPageNumber?: boolean;
}

// high → vert, medium → ambre, low / inconnu → rouge.
const PELLET_META: Record<
  FieldConfidence['confidence'] | 'unknown',
  { color: string; label: string }
> = {
  high: { color: '#10b981', label: 'Confiance élevée' },      // emerald-500
  medium: { color: '#f59e0b', label: 'Confiance moyenne' },   // amber-500
  low: { color: '#ef4444', label: 'Confiance faible' },       // red-500
  unknown: { color: '#ef4444', label: 'Aucune source' },      // rouge : pas de snippet/confidence
};

const Pellet: React.FC<{ color: string }> = ({ color }) => (
  <span
    aria-hidden
    style={{
      display: 'inline-block',
      width: '0.7rem',
      height: '0.7rem',
      borderRadius: '9999px',
      backgroundColor: color,
      flexShrink: 0,
    }}
  />
);

const ProvenanceTooltip: React.FC<ProvenanceTooltipProps> = ({
  snippet,
  page,
  confidence,
  fieldLabel,
  onClick,
}) => {
  const hasSnippet = !!snippet;
  // Pastille : la couleur suit la CONFIANCE du champ dès qu'elle existe — y
  // compris pour un champ VIDE. Un null justifié par le LLM (« information
  // certainement absente », confidence high) est une BONNE réponse → vert, pas
  // rouge. Avant, l'absence de snippet forçait 'unknown' (rouge) même sur ces
  // vides légitimes, en contradiction visuelle avec le score (cf. v12 = 100 %
  // mais pastilles rouges). On ne tombe sur 'unknown' (rouge « Aucune source »)
  // que s'il n'y a NI snippet NI confidence (extraction legacy).
  const level = confidence?.confidence ?? 'unknown';
  const pellet = PELLET_META[level];

  // Indice de confiance (high/medium/low) affiché dans l'en-tête.
  const confidenceLabel = confidence?.confidence
    ? confidence.confidence.toUpperCase()
    : '—';

  const heading = (
    <div className="flex items-center gap-2">
      <Pellet color={pellet.color} />
      <span className="font-semibold text-blue-300">{fieldLabel ?? 'Champ'}</span>
      <span className="text-xs text-gray-300">({confidenceLabel})</span>
    </div>
  );

  // Corps : le snippet si présent (avec sa page), sinon le message de confiance.
  const body = hasSnippet ? (
    <>
      {page != null && <p className="font-medium mb-1 text-blue-300">Page {page}</p>}
      <p className="italic text-gray-200 whitespace-pre-wrap">"{snippet}"</p>
    </>
  ) : (
    <p className="text-gray-200 whitespace-pre-wrap">
      {confidence?.reason?.trim()
        ? confidence.reason
        : 'Aucune source citée pour ce champ.'}
    </p>
  );

  const trigger = (
    <span className="flex items-center cursor-pointer" aria-label={pellet.label}>
      <Pellet color={pellet.color} />
    </span>
  );

  // Avec snippet : le clic navigue vers le PDF ET épingle la fenêtre.
  // ⚠️ On utilise un <span role="button"> (PAS un <button>) : la consultation
  // de provenance reste possible même quand le champ est en lecture seule —
  // un <fieldset disabled> (version antérieure / lock) désactiverait un
  // <button>, mais pas un <span>.
  if (hasSnippet && onClick) {
    return (
      <Tooltip content={body} pinnable headingNode={heading} maxWidth="max-w-md">
        <span
          role="button"
          tabIndex={0}
          onClick={() => onClick()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onClick();
          }}
          className="flex items-center cursor-pointer"
          aria-label={`${pellet.label} — voir la source`}
        >
          <Pellet color={pellet.color} />
        </span>
      </Tooltip>
    );
  }

  // Sans snippet (ou sans action) : la fenêtre s'ouvre/s'épingle, pas de navigation.
  return (
    <Tooltip content={body} pinnable headingNode={heading} maxWidth="max-w-md">
      {trigger}
    </Tooltip>
  );
};

export default ProvenanceTooltip;
